import {
  ICertificate,
  ICertificateDownload,
  ICertificateEnrollment,
  IIssueCertificate,
  IIssuedCertificate
} from 'interfaces/course_certificate';
import { ISignaturePartnersGet } from 'interfaces/signature_partners';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { getAPI } from 'requests/api';

// Types
interface CertificateState {
  // Data state - Agora será gerenciado pelos hooks do Refine
  enrollment: ICertificateEnrollment | null;
  certificate: ICertificate | null;
  signaturePartner: ISignaturePartnersGet | null;

  // Loading states para operações customizadas
  isIssuingCertificate: boolean;
  isDeletingCertificate: boolean;

  // UI states
  modals: {
    createCertificate: boolean;
    newCertificate: boolean;
    rejectCertificate: boolean;
    observation: {
      title: string;
      date: string;
      autor: string;
      observation: string;
    };
  };
  drawers: {
    emissions: boolean;
    approves: boolean;
    approvesHistory: boolean;
  };

  // Computed states
  hasCertificate: boolean;
  canIssueCertificate: boolean;

  // Polling state
  pollingInterval: NodeJS.Timeout | null;
}

interface CertificateActions {
  // Data actions - Mantidos para compatibilidade com componentes
  setEnrollment: (enrollment: ICertificateEnrollment | null) => void;
  setCertificate: (certificate: ICertificate | null) => void;
  setSignaturePartner: (signaturePartner: ISignaturePartnersGet | null) => void;

  // Modal actions
  openCreateCertificateModal: () => void;
  closeCreateCertificateModal: () => void;
  openNewCertificateModal: () => void;
  closeNewCertificateModal: () => void;
  openRejectCertificateModal: () => void;
  closeRejectCertificateModal: () => void;
  openObservationModal: (
    title: string,
    date: string,
    autor: string,
    observation: string
  ) => void;
  closeObservationModal: () => void;
  // Drawer actions
  openEmissionsDrawer: () => void;
  closeEmissionsDrawer: () => void;
  openApprovesDrawer: () => void;
  closeApprovesDrawer: () => void;
  openApprovesHistoryDrawer: () => void;
  closeApprovesHistoryDrawer: () => void;

  // API actions customizadas (que não seguem padrão REST do Refine)
  issueCertificate: (
    data: IIssueCertificate,
    certificateId?: string,
    callbacks?: {
      onProcessing?: () => void;
      onProcessed?: () => void;
      onError?: () => void;
    }
  ) => Promise<void>;
  downloadCertificate: (certificate: ICertificate) => Promise<void>;
  deleteAndReissueCertificate: (
    certificateId: string,
    observations: string
  ) => Promise<void>;

  // Polling actions
  startCertificatePolling: (
    certificateId: string,
    callbacks?: {
      onProcessed?: () => void;
      onError?: () => void;
    }
  ) => void;
  stopCertificatePolling: () => void;

  // Utility actions
  reset: () => void;
  // Função para refresh completo após operações críticas
  refreshCurrentPage: () => void;
}

// Constants
const CERTIFICATE_ENDPOINTS = {
  CERTIFICATES: '/admin/graduate/courses/certificates',
  CERTIFICATE_BY_ID: (id: string) =>
    `/admin/graduate/courses/certificates/${id}`,
  DOWNLOAD: (id: string) =>
    `/admin/graduate/courses/certificates/${id}/download`,
  ENROLLMENT: (id: string) => `/admin/graduate/courses/enrollments/${id}`,
  SIGNATURE_PARTNER: (id: string) =>
    `/admin/certifiers/signature_partners/${id}`
} as const;

const POLLING_INTERVAL = 5000; // 5 segundos

// Helper functions
const handleDownloadURL = (certificateData: ICertificateDownload): void => {
  if (certificateData.url) {
    window.open(certificateData.url, '_self');
  }
};

export const useCertificateStore = create<
  CertificateState & CertificateActions
>()(
  persist(
    (set, get) => ({
      // Initial state
      enrollment: null,
      certificate: null,
      signaturePartner: null,
      isIssuingCertificate: false,
      isDeletingCertificate: false,
      modals: {
        createCertificate: false,
        newCertificate: false,
        rejectCertificate: false,
        observation: { title: '', date: '', autor: '', observation: '' }
      },
      drawers: {
        emissions: false,
        approves: false,
        approvesHistory: false
      },
      hasCertificate: false,
      canIssueCertificate: false,

      // Polling state
      pollingInterval: null as NodeJS.Timeout | null,

      // Data actions
      setEnrollment: enrollment => {
        const hasCertificate = enrollment
          ? enrollment.certificate_id !== null &&
            enrollment.certificate_status === 'issued'
          : false;

        const canIssueCertificate =
          enrollment?.certificate_status === 'approved';

        set({
          enrollment,
          hasCertificate,
          canIssueCertificate
        });
      },

      setCertificate: certificate => set({ certificate }),

      setSignaturePartner: signaturePartner => set({ signaturePartner }),

      // Modal actions
      openCreateCertificateModal: () =>
        set(state => ({
          modals: { ...state.modals, createCertificate: true }
        })),

      closeCreateCertificateModal: () =>
        set(state => ({
          modals: { ...state.modals, createCertificate: false }
        })),

      openNewCertificateModal: () =>
        set(state => ({
          modals: { ...state.modals, newCertificate: true }
        })),

      closeNewCertificateModal: () =>
        set(state => ({
          modals: { ...state.modals, newCertificate: false }
        })),

      // Drawer actions
      openEmissionsDrawer: () =>
        set(state => ({
          drawers: { ...state.drawers, emissions: true }
        })),

      closeEmissionsDrawer: () =>
        set(state => ({
          drawers: { ...state.drawers, emissions: false }
        })),

      openApprovesDrawer: () =>
        set(state => ({
          drawers: { ...state.drawers, approves: true }
        })),

      closeApprovesDrawer: () =>
        set(state => ({
          drawers: { ...state.drawers, approves: false }
        })),

      openRejectCertificateModal: () =>
        set(state => ({
          modals: { ...state.modals, rejectCertificate: true }
        })),

      closeRejectCertificateModal: () =>
        set(state => ({
          modals: { ...state.modals, rejectCertificate: false }
        })),

      openObservationModal: (title, date, autor, observation) =>
        set(state => ({
          modals: {
            ...state.modals,
            observation: { title, date, autor, observation }
          }
        })),

      closeObservationModal: () =>
        set(state => ({
          modals: {
            ...state.modals,
            observation: { title: '', date: '', autor: '', observation: '' }
          }
        })),

      openApprovesHistoryDrawer: () =>
        set(state => ({
          drawers: { ...state.drawers, approvesHistory: true }
        })),

      closeApprovesHistoryDrawer: () =>
        set(state => ({
          drawers: { ...state.drawers, approvesHistory: false }
        })),

      // API actions customizadas
      issueCertificate: async (
        data: IIssueCertificate,
        certificateId?: string,
        callbacks?: {
          onProcessing?: () => void;
          onProcessed?: () => void;
          onError?: () => void;
        }
      ) => {
        set({ isIssuingCertificate: true });

        try {
          const response = certificateId
            ? await getAPI().put<IIssuedCertificate>(
                CERTIFICATE_ENDPOINTS.CERTIFICATE_BY_ID(certificateId),
                {
                  issued_at: data.issued_at,
                  registered_at: data.registered_at,
                  signature_partner_id: data.signature_partner_id,
                  observations: data.observations
                }
              )
            : await getAPI().post<IIssuedCertificate>(
                CERTIFICATE_ENDPOINTS.CERTIFICATES,
                data
              );

          if (response?.data) {
            // Atualizar o status do enrollment para 'processing'
            const currentEnrollment = get().enrollment;

            if (currentEnrollment) {
              set({
                enrollment: {
                  ...currentEnrollment,
                  certificate_status: 'processing',
                  certificate_id: response.data.id
                }
              });
            }

            // Chamar callback de processamento
            callbacks?.onProcessing?.();

            // Iniciar polling para verificar status do certificado
            get().startCertificatePolling(response.data.id, {
              onProcessed: callbacks?.onProcessed,
              onError: callbacks?.onError
            });
          }
        } catch (error) {
          console.error('Error issuing certificate:', error);
          throw error;
        } finally {
          set({ isIssuingCertificate: false });
        }
      },

      downloadCertificate: async (certificate: ICertificate) => {
        try {
          const response = await getAPI().get<ICertificateDownload>(
            CERTIFICATE_ENDPOINTS.DOWNLOAD(certificate.id)
          );
          handleDownloadURL(response.data);
        } catch (error) {
          console.error('Error downloading certificate:', error);
          throw error;
        }
      },

      deleteAndReissueCertificate: async (
        certificateId: string,
        observations: string
      ) => {
        set({ isDeletingCertificate: true });

        try {
          await getAPI().delete(
            `${CERTIFICATE_ENDPOINTS.CERTIFICATE_BY_ID(
              certificateId
            )}?observations=${observations}`
          );
          // O refresh será feito via invalidateQueries no componente
        } catch (error) {
          console.error('Error deleting and reissuing certificate:', error);
          throw error;
        } finally {
          set({ isDeletingCertificate: false });
        }
      },

      // Polling actions
      startCertificatePolling: (
        certificateId: string,
        callbacks?: {
          onProcessed?: () => void;
          onError?: () => void;
        }
      ) => {
        // Parar polling anterior se existir
        const currentInterval = get().pollingInterval;

        if (currentInterval) {
          clearInterval(currentInterval);
        }

        const pollCertificateStatus = async () => {
          try {
            const response = await getAPI().get<ICertificate>(
              CERTIFICATE_ENDPOINTS.CERTIFICATE_BY_ID(certificateId)
            );

            if (response.data.status === 'processed') {
              // Certificado processado - atualizar status para 'issued'
              const currentEnrollment = get().enrollment;

              if (currentEnrollment) {
                set({
                  enrollment: {
                    ...currentEnrollment,
                    certificate_status: 'issued'
                  },
                  certificate: response.data
                });
              }

              // Parar o polling
              get().stopCertificatePolling();

              // Chamar callback de sucesso
              callbacks?.onProcessed?.();
            } else if (response.data.status === 'error') {
              // Erro no processamento
              const currentEnrollment = get().enrollment;

              if (currentEnrollment) {
                set({
                  enrollment: {
                    ...currentEnrollment,
                    certificate_status: 'rejected'
                  }
                });
              }

              // Parar o polling
              get().stopCertificatePolling();

              // Chamar callback de erro
              callbacks?.onError?.();
            }
            // Se status for 'processing' ou outro, continuar polling
          } catch (error) {
            console.error('Error polling certificate status:', error);
            // Em caso de erro na requisição, parar o polling após algumas tentativas
            get().stopCertificatePolling();
          }
        };

        // Executar primeira verificação imediatamente
        pollCertificateStatus();

        // Configurar interval
        const interval = setInterval(pollCertificateStatus, POLLING_INTERVAL);
        set({ pollingInterval: interval });
      },

      stopCertificatePolling: () => {
        const currentInterval = get().pollingInterval;

        if (currentInterval) {
          clearInterval(currentInterval);
          set({ pollingInterval: null });
        }
      },

      // Utility actions
      reset: () => {
        // Parar polling se estiver ativo
        const currentInterval = get().pollingInterval;

        if (currentInterval) {
          clearInterval(currentInterval);
        }

        set({
          enrollment: null,
          certificate: null,
          signaturePartner: null,
          isIssuingCertificate: false,
          modals: {
            createCertificate: false,
            newCertificate: false,
            rejectCertificate: false,
            observation: { title: '', date: '', autor: '', observation: '' }
          },
          drawers: {
            emissions: false,
            approves: false,
            approvesHistory: false
          },
          hasCertificate: false,
          canIssueCertificate: false,
          pollingInterval: null
        });
      },

      // Função para refresh completo da página atual
      refreshCurrentPage: () => {
        window.location.reload();
      }
    }),
    {
      name: 'certificate-storage',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist essential data, not loading states or UI states
      partialize: state => ({
        enrollment: state.enrollment,
        certificate: state.certificate,
        hasCertificate: state.hasCertificate,
        canIssueCertificate: state.canIssueCertificate
      })
    }
  )
);
